import {
    type ChangeEvent,
    type DragEvent,
    type MouseEvent,
    type ReactNode,
    type Ref,
    useId,
    useRef,
    useState,
} from 'react';
import { Icon } from '../../icons/Icon';
import type { IconName } from '../../icons/registry';
import styles from './FileUpload.module.css';

/** The states a caller sets. Dragover is the component's own, while a file is dragged over it. */
export const FILE_UPLOAD_STATES = ['idle', 'uploading', 'failed'] as const;
export type FileUploadState = (typeof FILE_UPLOAD_STATES)[number];

export interface FileUploadFailure {
    /** The reason, as the user should read it — what to do next, not the exception. */
    readonly message: string;
    /** The ways on: choose another file, try again. */
    readonly actions?: ReactNode;
}

export interface FileUploadProps {
    /**
     * What is being chosen ("Workbook file"). It completes the input's accessible name after the
     * visible **Browse** ("Browse: Workbook file"), so the name a screen reader reads starts with the
     * word a sighted user sees (SC 2.5.3).
     */
    readonly label: string;
    /** The input's `accept`, such as `.xlsx`. The caller still checks what it receives. */
    readonly accept: string;
    /** The accepted type and the size cap, stated before upload (§5): "Excel workbook (.xlsx), up to 10 MB". */
    readonly hint: string;
    readonly state: FileUploadState;
    /** What the uploading state says beside its progress bar ("Checking the workbook"). */
    readonly uploadingLabel?: string;
    /** Shown in the failed state, in `tone/danger`, announced as it appears. */
    readonly failure?: FileUploadFailure;
    /** Every file chosen or dropped at once. Refusing more than one is the caller's rule. */
    readonly onFiles: (files: readonly File[]) => void;
    /**
     * Nothing can be chosen yet. `aria-disabled` plus a refused click and drop, never the bare
     * attribute (§5's Disabled row), with the reason read out from `disabledReason`.
     */
    readonly disabled?: boolean;
    /** Why it is disabled — required by §9's rule when `disabled` is set. */
    readonly disabledReason?: string;
    /** The real `<input type="file">`, for a caller whose own action opens the picker again. */
    readonly inputRef?: Ref<HTMLInputElement>;
    /** Several files at once: the input's `multiple`, and the prompt reads "Drop files here, or". */
    readonly multiple?: boolean;
    /**
     * The visible control's word in place of **Browse** ("Attach files"); the accessible name stays
     * `<browseLabel>: <label>`, starting with the word a sighted user sees.
     */
    readonly browseLabel?: string;
    /** The zone's glyph, by concept. Defaults to `upload`. */
    readonly icon?: IconName;
    /** The drop zone's test hook; it carries `data-state`. The input takes `<hook>-input`, the failure region `<hook>-error`. */
    readonly 'data-testid'?: string;
}

/** What the zone shows: the caller's state, or dragover while a drag is over an available zone. */
type ShownState = FileUploadState | 'dragover';

function filesOf(list: FileList | null): readonly File[] {
    return list === null ? [] : Array.from(list);
}

/**
 * Design-system §5 *File upload*: a drop zone with a **real `<input type="file">` behind it**, so
 * nobody depends on a drag gesture. The input is visually hidden but stays focusable; its visible
 * **Browse** label draws the button and the focus ring, so Tab reaches the input and Space or Enter
 * opens the browser's picker, and a click on the label does the same.
 *
 * States: idle, dragover (`accent/gold` border), uploading and failed (`tone/danger`, the reason and
 * the caller's actions, `role="alert"`). Two §5 departures, each the first consumer's (F26-c
 * WI-1.2 Decisions 9 and 19): the uploading bar is **indeterminate**, because `fetch` reports no
 * upload progress; and there is **no scanning state**, because nothing uploaded here is stored —
 * the first consumer that stores a file (F33, FS-11) adds it.
 *
 * A caller uploading **several files** (`multiple`, F20-h's compose dialog) keeps the zone `idle` and
 * draws a determinate bar and the scan state per file, in its own attachment rows; the zone's own
 * uploading bar stays the indeterminate one for single-file `postForm` callers.
 */
export function FileUpload({
    label,
    accept,
    hint,
    state,
    uploadingLabel = 'Uploading',
    failure,
    onFiles,
    disabled = false,
    disabledReason,
    inputRef,
    multiple = false,
    browseLabel = 'Browse',
    icon = 'upload',
    'data-testid': testId,
}: FileUploadProps) {
    const inputId = useId();
    const hintId = useId();
    const reasonId = useId();
    const progressId = useId();
    const [dragging, setDragging] = useState(false);
    // Enters minus leaves: moving onto a child of the zone fires the child's enter before the zone's
    // leave, so the count stays above zero until the drag has really left.
    const dragDepth = useRef(0);

    const unavailable = disabled || state === 'uploading';
    const shown: ShownState = dragging && !unavailable ? 'dragover' : state;
    const describedBy = disabled && disabledReason !== undefined ? `${hintId} ${reasonId}` : hintId;

    function handleDragEnter(event: DragEvent<HTMLDivElement>) {
        event.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
    }

    function handleDragOver(event: DragEvent<HTMLDivElement>) {
        // Without this the browser opens the dropped file itself instead of handing it over.
        event.preventDefault();
        event.dataTransfer.dropEffect = unavailable ? 'none' : 'copy';
    }

    function handleDragLeave() {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) {
            setDragging(false);
        }
    }

    function handleDrop(event: DragEvent<HTMLDivElement>) {
        event.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        const files = filesOf(event.dataTransfer.files);
        if (!unavailable && files.length > 0) {
            onFiles(files);
        }
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const files = filesOf(event.target.files);
        // Cleared so choosing the same file again — after a refusal, say — still reports it.
        event.target.value = '';
        if (!unavailable && files.length > 0) {
            onFiles(files);
        }
    }

    function handleClick(event: MouseEvent<HTMLInputElement>) {
        if (unavailable) {
            // An aria-disabled input still opens the picker on activation; this is what stops it.
            event.preventDefault();
        }
    }

    return (
        <div className={styles.wrapper}>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: the drop zone is a pointer-only shortcut; the real file input inside it is the keyboard and screen-reader path (§5), so the zone itself is no control and takes no role */}
            {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: as above — the drag handlers duplicate what the labelled input already offers */}
            <div
                className={styles.zone}
                data-state={shown}
                data-testid={testId}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                <span className={styles.glyph} data-icon={icon}>
                    <Icon name={icon} size="md" />
                </span>
                {state === 'uploading' ? (
                    <div className={styles.uploading}>
                        <span className={styles.prompt} id={progressId}>
                            {uploadingLabel}
                        </span>
                        <progress aria-labelledby={progressId} className={styles.progress} />
                    </div>
                ) : (
                    <p className={styles.prompt}>
                        {multiple ? 'Drop files here, or' : 'Drop the file here, or'}
                    </p>
                )}
                <div className={styles.browseRow}>
                    <input
                        accept={accept}
                        aria-describedby={describedBy}
                        aria-disabled={unavailable}
                        className={styles.input}
                        data-testid={testId === undefined ? undefined : `${testId}-input`}
                        id={inputId}
                        multiple={multiple}
                        onChange={handleChange}
                        onClick={handleClick}
                        ref={inputRef}
                        type="file"
                    />
                    <label className={styles.browse} htmlFor={inputId}>
                        {browseLabel}
                        <span className={styles.visuallyHidden}>: {label}</span>
                    </label>
                </div>
                <p className={styles.hint} id={hintId}>
                    {hint}
                </p>
                {disabled && disabledReason !== undefined ? (
                    <p className={styles.hint} id={reasonId}>
                        {disabledReason}
                    </p>
                ) : null}
            </div>
            {state === 'failed' && failure !== undefined ? (
                <div
                    className={styles.failure}
                    data-testid={testId === undefined ? undefined : `${testId}-error`}
                    role="alert"
                >
                    <span className={styles.failureGlyph}>
                        <Icon name="error" size="md" />
                    </span>
                    <p className={styles.failureMessage}>{failure.message}</p>
                    {failure.actions === undefined ? null : (
                        <div className={styles.actions}>{failure.actions}</div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
